import { appConfig } from '../../constants/app-config';
import { loadingBarModule } from '../../vendor/loading-bar-module';
import { ngFileUploadComponentModule } from '../../vendor/ng-file-upload-component-module';
import {
  IMAGE_FILE_EXTENSION_REGEX,
  IMAGE_MIME_TYPE_REGEX,
} from '../../utils/file-utils/image-file-utils';

export const fileUploadServiceModule = angular
  .module('Q.services.file-upload-service', [
    ngFileUploadComponentModule,
    loadingBarModule,
  ])

  .factory(
    'fileUploadService',
    function fileUploadService(Upload, cfpLoadingBar, $window, $q, $http) {
      let activeFileProgress = [];

      const DEFAULT_ERROR_MESSAGE = 'Unable to upload file';

      function _setActiveProgress(final) {
        const sum = activeFileProgress.reduce((s, v) => s + v, 0);
        const len = activeFileProgress.length;
        if (sum === len && final) {
          activeFileProgress.length = 0;
          cfpLoadingBar.complete();
        } else if (len > 0) {
          cfpLoadingBar.start();
          cfpLoadingBar.set(sum / len);
        }
      }

      /**
       * Uploads a single file
       * @param file {File} File object passed in from a browser API
       * @param rejected {Array} List of rejected files (for error reporting)
       * @param validator {Function} Validator used for checking the files
       * @param showProgress {boolean} Set to false to hide the loading bar for progress
       * @returns {Promise}
       */
      function handleSingle(
        file,
        { rejected = [], validator = null, showProgress = true } = {},
      ) {
        let upload;
        if (angular.isArray(file)) file = file[0];
        if (!file) {
          let msg = null;
          if (rejected.length) {
            msg = 'No valid files selected.';
            if (validator && validator.label) {
              msg += ` You can use ${validator.label}.`;
            }
          }
          return $q.reject(msg);
        }

        if (file.size && file.size >= 15728640) {
          return $q.reject('Please only upload files smaller than 15Mb');
        }

        const activeFileIndex = activeFileProgress.length;
        if (showProgress) {
          activeFileProgress[activeFileIndex] = 0;
          _setActiveProgress();
        }

        let uploadDeferred = $q.defer();

        if (angular.isString(file)) {
          upload = $http.post(
            appConfig.uploadRoute,
            { file: file },
            { ignoreLoadingBar: true },
          );
        } else {
          upload = Upload.upload({
            url: appConfig.uploadRoute,
            data: { file: file },
            ignoreLoadingBar: true,
          }).progress((evt) => {
            let progress = (0.95 * evt.loaded) / evt.total;
            if (showProgress) {
              // max 95% for file uploads, leaving the remaining 5% for brightness, etc
              activeFileProgress[activeFileIndex] = progress;
              _setActiveProgress();
            }
            uploadDeferred.notify(progress);
          });
        }

        upload
          .then((resp) => {
            let files = resp.data.files;
            let error = false;

            if (!files.length) {
              // no files were returned
              error = true;
            } else {
              let file = files[0];
              if (file.success !== true) {
                // server reports an error for this image
                if (file.s3 && file.s3.err) {
                  // if we have an S3 error, we're done
                  error = file.s3.err;
                } else if (validator === imageValidator) {
                  // only worry about cloudinary errors on image uploads
                  error = true;
                  if (file.cloudinary && file.cloudinary.error) {
                    error = file.cloudinary.error.message;
                  }
                }
              }
            }

            return error === false ? resp : $q.reject(error);
          })
          .then((resp) => {
            return resultToModel(resp.data.files[0]);
          })
          .catch((resp) => {
            let reason;
            if (!resp) {
              reason = DEFAULT_ERROR_MESSAGE;
            } else if (angular.isString(resp)) {
              resp = {
                reason: resp,
              };
            } else {
              let data = resp.data;
              if (data && data.errors) {
                reason = data.errors[0] || DEFAULT_ERROR_MESSAGE;
              } else if (!resp.reason && !resp.statusText) {
                reason = DEFAULT_ERROR_MESSAGE;
              }
            }
            return $q.reject(reason ? { data: { reason } } : resp);
          })
          .finally(() => {
            if (showProgress) {
              activeFileProgress[activeFileIndex] = 1;
              _setActiveProgress(true);
            }
            uploadDeferred.notify(1);
          })
          .then(uploadDeferred.resolve, uploadDeferred.reject);

        return uploadDeferred.promise;
      }

      function loadImagePreview(file) {
        // don't preview if we can't read it, it's not an image, it's a string, or the file size is over 10Mb
        if (
          $window.FileReader === undefined ||
          !previewImageValidator(file) ||
          angular.isString(file) ||
          file.size > 1e7
        ) {
          return $q.reject();
        }

        return $q((resolve) => {
          const reader = new FileReader();
          reader.onload = function (evt) {
            resolve(evt.target.result);
          };
          reader.readAsDataURL(file);
        });
      }

      function previewImageValidator(file) {
        return file && /\.(jpg|jpeg|gif|png|svg)$/.test(file.name);
      }

      previewImageValidator.label = 'simple images, such as jpg, png, or gif';

      function imageValidator(file) {
        return (
          file &&
          (IMAGE_MIME_TYPE_REGEX.test(file.type || '') ||
            IMAGE_FILE_EXTENSION_REGEX.test(file.name || ''))
        );
      }

      imageValidator.label = 'most images, including jpg, png, or psd';

      function documentValidator(file) {
        return (
          file &&
          /\.(docx?|pdf|rtf|txt|pptx?|ppsx?|html?|odt|pages|md|markdown)$/i.test(
            file.name,
          )
        );
      }

      documentValidator.label =
        'common document formats, such as docx, pdf, rtf, html, or even plain text';

      /**
       * Converts a server upload result into a storable document upload model
       * @param result
       * @returns {{fileId: *, fileFormat: *, width: null, height: null, resourceType: *|null, size: _bi255.bytes|*|{base: number, units: string[]}|FUZZY_NUMBER_TYPES.bytes|{base, units}}}
       */
      function resultToModel(result) {
        const s3Id = result.s3.name.replace(/\..*/, '');
        const s3Format = result.s3.name.replace(/.*\./, '');
        return {
          fileId: result.cloudinary.public_id || s3Id,
          fileFormat: result.cloudinary.format || s3Format,
          // pass back nulls so the server clears existing values
          width: result.cloudinary.width || null,
          height: result.cloudinary.height || null,
          resourceType: result.cloudinary.resource_type || null,
          size: result.cloudinary.bytes || result.s3.size,
        };
      }

      /**
       * Gets the raw download URL for an uploaded image
       * @param model
       * @returns {*}
       */
      function downloadUrl(model) {
        if (!model || (!model.fileId && !model.rawUrl)) return null;
        if (model.rawUrl) {
          return model.rawUrl;
        } else if (model.fileFormat && !/\.\w+$/.test(model.fileId)) {
          return `${appConfig.s3BaseUrl}${model.fileId}${model.fileFormat ? '.' + model.fileFormat : ''}`;
        } else {
          return `${appConfig.s3BaseUrl}${model.fileId}`;
        }
      }

      /**
       * Preloads an image for display
       * @param imageUrl
       */
      function preloadImage(imageUrl) {
        let img;
        return $q((resolve, reject) => {
          img = document.createElement('img');
          img.src = imageUrl;
          img.style.display = 'none';
          document.body.appendChild(img);

          img.onload = () => resolve(img);
          img.onerror = () => reject();
          // catch that sometimes the image is loaded already
          if (img.complete) {
            resolve(img);
          }
        }).finally(() => {
          if (img) {
            angular.element(img).remove();
          }
        });
      }

      return {
        handleSingle,
        imageValidator,
        documentValidator,
        loadImagePreview,
        downloadUrl,
        preloadImage,
      };
    },
  )

  .filter('downloadUrl', function (fileUploadService) {
    return function (model) {
      return fileUploadService.downloadUrl(model);
    };
  });
